import { getDefaultFixtureForComponent } from '@web-studio/core-fixtures';
import { listImplementedComponentTypes } from '@web-studio/layout-matrix';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { buildStaticParityReport } from '@/lib/parityCheck';

/**
 * Runs SSR checkup for each implemented block (default fixture, michelin-dark).
 * Uses static React `SitePage` markup vs `renderSiteHtml` — not the live canvas.
 */
export function BatchParityPage() {
  const results = useMemo(() => {
    const themeId = 'michelin-dark';
    const colorsetId = 'champagne_night';
    const locale = 'en';

    return listImplementedComponentTypes().map((componentType) => {
      const fixture = getDefaultFixtureForComponent(componentType);
      if (!fixture) {
        return { componentType, status: 'no_fixture' as const };
      }

      const blocks = [fixture.block];
      const report = buildStaticParityReport(blocks, locale, themeId, colorsetId);
      return {
        componentType,
        status: 'ok' as const,
        report,
        blocks,
      };
    });
  }, []);

  return (
    <div className="lab-page">
      <header className="lab-page-header">
        <h1 className="lab-page-header__title">Batch Checkup</h1>
        <p className="lab-page-header__desc">
          Static React <code>SitePage</code> (editable:false) vs <code>renderSiteHtml</code>{' '}
          &lt;main&gt; · default fixture · michelin-dark / champagne_night. Whitespace and attribute
          order are normalized. Structural = block id/type order; HTML = markup equality after
          normalize.
        </p>
      </header>

      <table className="lab-table">
        <thead>
          <tr>
            <th>Block</th>
            <th>Structural</th>
            <th>HTML</th>
            <th>React / SSR chars</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {results.map((row) => (
            <tr key={row.componentType}>
              <td>
                <code>{row.componentType}</code>
              </td>
              {row.status === 'no_fixture' ? (
                <>
                  <td>
                    <span className="lab-badge lab-badge--muted">no_fixture</span>
                  </td>
                  <td>—</td>
                  <td>—</td>
                  <td />
                </>
              ) : (
                <>
                  <td>
                    <span
                      className={`lab-badge ${row.report.structuralMatch ? 'lab-badge--ok' : 'lab-badge--warn'}`}
                    >
                      {row.report.structuralMatch ? 'match' : 'mismatch'}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`lab-badge ${row.report.matches ? 'lab-badge--ok' : 'lab-badge--warn'}`}
                    >
                      {row.report.matches ? 'match' : 'mismatch'}
                    </span>
                  </td>
                  <td>
                    {row.report.reactLength} / {row.report.ssrLength}
                  </td>
                  <td>
                    <Link
                      to={`/matrix/block/${row.componentType}?mode=engineer&parity=1&targets=1`}
                      className="text-xs no-underline"
                      style={{ color: 'var(--lab-accent)' }}
                    >
                      Live checkup →
                    </Link>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <p className="text-xs" style={{ color: 'var(--lab-muted)' }}>
        Structural mismatch = fix SSR/React block wiring first. HTML mismatch = markup drift (use
        Live checkup for a side-by-side excerpt). Live canvas checkup may still differ slightly from
        this static check.
      </p>
    </div>
  );
}
